/**
 * Draggable divider between lesson and whiteboard panels.
 * Tutor can drag to resize; onDrag updates local layout, onDragEnd sends to child.
 */

import React, { useCallback, useRef, useState } from 'react';
import './ResizableDivider.css';

const DIVIDER_WIDTH = 6;
const MIN_RATIO = 0.15;
const MAX_RATIO = 0.7;

function ResizableDivider({ whiteboardRatio, onDrag, onDragEnd, canResize = true }) {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startRatioRef = useRef(0);
  const lastRatioRef = useRef(whiteboardRatio);

  const handlePointerDown = useCallback(
    (e) => {
      if (!canResize) return;
      e.preventDefault();
      setIsDragging(true);
      startXRef.current = e.clientX;
      startRatioRef.current = whiteboardRatio;
    },
    [canResize, whiteboardRatio]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const container = containerRef.current?.parentElement;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const totalWidth = rect.width;
      if (totalWidth <= 0) return;

      const deltaX = e.clientX - startXRef.current;
      const deltaRatio = -deltaX / totalWidth;
      let newRatio = startRatioRef.current + deltaRatio;
      newRatio = Math.max(MIN_RATIO, Math.min(MAX_RATIO, newRatio));
      lastRatioRef.current = newRatio;
      onDrag?.(newRatio);
    },
    [isDragging, onDrag]
  );

  const handlePointerUp = useCallback(() => {
    if (isDragging && onDragEnd) {
      onDragEnd(lastRatioRef.current);
    }
    setIsDragging(false);
  }, [isDragging, onDragEnd]);

  React.useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => handlePointerMove(e);
    const onUp = () => handlePointerUp();
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handlePointerMove, handlePointerUp]);

  return (
    <div
      ref={containerRef}
      className={`resizable-divider ${canResize ? 'resizable-divider--draggable' : ''} ${isDragging ? 'resizable-divider--active' : ''}`}
      style={{ width: DIVIDER_WIDTH }}
      onPointerDown={handlePointerDown}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(whiteboardRatio * 100)}
    />
  );
}

export default React.memo(ResizableDivider);
